import * as React from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { 
    ArrowUpIcon, 
    FileTextIcon, 
    ShieldIcon, 
    ScaleIcon, 
    AlertCircleIcon,
    LockIcon,
    BellIcon,
    MailIcon
} from 'lucide-react'
import { Link, useNavigate } from "react-router-dom"
import { useTenant } from "@/context/TenantContext"
import { useTheme } from "@/context/ThemeContext"

export default function TermsOfService() {
    const [showScrollTop, setShowScrollTop] = React.useState(false)
    const [activeSection, setActiveSection] = React.useState("")
    const { branding } = useTenant()
    const { theme } = useTheme()
    const navigate = useNavigate()

    // Determine if we should use dark logo
    const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const effectiveLogoUrl = isDarkMode && branding?.dark_logo_url ? branding.dark_logo_url : branding?.logo_url;

    const renderLogo = () => {
        if (!effectiveLogoUrl) {
            return (
                <span className="text-2xl font-semibold text-primary">
                    {branding?.company_name || 'VoiceBot'}
                </span>
            );
        }

        return (
            <img 
                src={effectiveLogoUrl}
                alt={branding?.company_name || 'Company Logo'}
                className="max-h-10 w-auto max-w-[160px] object-contain cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate('/')}
                onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    const parent = target.parentElement;
                    if (parent) {
                        parent.innerHTML = `<span class="text-2xl font-semibold text-primary cursor-pointer">${branding?.company_name || 'VoiceBot'}</span>`;
                        parent.onclick = () => navigate('/');
                    }
                }}
            />
        );
    };

    React.useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.pageYOffset > 300)

            const sections = document.querySelectorAll("section[id]")
            let currentActiveSection = ""

            sections.forEach((section) => {
                const sectionTop = (section as HTMLElement).offsetTop
                const sectionHeight = section.clientHeight
                if (window.pageYOffset >= sectionTop - 100 && window.pageYOffset < sectionTop + sectionHeight - 100) {
                    currentActiveSection = section.id
                }
            })

            if (window.innerHeight + window.pageYOffset >= document.body.offsetHeight - 100) {
                currentActiveSection = sections[sections.length - 1].id
            }

            setActiveSection(currentActiveSection)
        }

        window.addEventListener('scroll', handleScroll)
        handleScroll() // Initial check
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const scrollToSection = (sectionId: string) => {
        const section = document.getElementById(sectionId)
        if (section) {
            const offset = 80
            const elementPosition = section.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.pageYOffset - offset

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            })
        }
    }

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.2,
                ease: "easeOut",
                staggerChildren: 0.03
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 8 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.25,
                ease: [0.22, 1, 0.36, 1] // Professional easing curve
            }
        }
    }

    const sections = [
        { id: "acceptance", title: "Acceptance of Terms", icon: FileTextIcon },
        { id: "description", title: "Description of Service", icon: AlertCircleIcon },
        { id: "user-accounts", title: "User Accounts", icon: LockIcon },
        { id: "intellectual-property", title: "Intellectual Property", icon: ShieldIcon },
        { id: "limitation-liability", title: "Limitation of Liability", icon: ScaleIcon },
        { id: "modifications", title: "Modifications to Service", icon: BellIcon },
        { id: "governing-law", title: "Governing Law", icon: ScaleIcon },
        { id: "changes", title: "Changes to Terms", icon: BellIcon },
        { id: "contact", title: "Contact Us", icon: MailIcon },
    ]

    const lastUpdated = "October 20, 2025"

    return (
        <div className="min-h-screen bg-muted/30 relative">
            {/* Animated gradient background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div 
                    className="absolute -top-[40%] -right-[20%] w-[60%] h-[60%] rounded-full bg-primary/[0.04] dark:bg-primary/[0.06] blur-3xl animate-pulse"
                    style={{ animationDuration: '8s' }}
                />
                <div 
                    className="absolute -bottom-[40%] -left-[20%] w-[60%] h-[60%] rounded-full bg-primary/[0.05] dark:bg-primary/[0.08] blur-3xl animate-pulse"
                    style={{ animationDuration: '8s', animationDelay: '4s' }}
                />
            </div>

            {/* Header */}
            <motion.header 
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
            >
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                    <div className="flex h-16 items-center justify-between">
                        <div className="flex items-center gap-8">
                            {renderLogo()}
                            <span className="hidden sm:inline text-sm text-muted-foreground">
                                Terms of Service
                            </span>
                        </div>
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(-1)}
                            className="text-sm"
                        >
                            Back
                        </Button>
                    </div>
                </div>
            </motion.header>

            {/* Main Content */}
            <div className="relative">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8 lg:py-12">
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                        {/* Hero Section */}
                        <motion.div variants={itemVariants} className="mb-12">
                            <div className="max-w-3xl">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                                    <FileTextIcon className="h-3.5 w-3.5" />
                                    Legal Document
                                </div>
                                <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
                                    Terms of Service
                                </h1>
                                <p className="text-lg text-muted-foreground mb-4">
                                    Please read these terms carefully before using our services. By accessing or using our platform, you agree to be bound by these terms.
                                </p>
                                <p className="text-sm text-muted-foreground/70">
                                    Last updated: {lastUpdated}
                                </p>
                            </div>
                </motion.div>

                        {/* Content Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                            {/* Sidebar Navigation - Hidden on mobile */}
                            <motion.aside 
                                variants={itemVariants}
                                className="hidden lg:block lg:col-span-1"
                            >
                                <div className="sticky top-24">
                                    <Card className="shadow-sm">
                                        <CardContent className="p-4">
                                            <h3 className="text-sm font-semibold mb-3">On this page</h3>
                                            <nav>
                                                <ul className="space-y-1">
                                                    {sections.map((section) => {
                                                        const Icon = section.icon
                                                        return (
                                                            <li key={section.id}>
                                                                <button
                                                                    onClick={() => scrollToSection(section.id)}
                                                                    className={`
                                                                        w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs
                                                                        transition-colors text-left
                                                                        ${activeSection === section.id 
                                                                            ? 'bg-primary/10 text-primary font-medium' 
                                                                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                                                        }
                                                                    `}
                                                                >
                                                                    <Icon className="h-3.5 w-3.5 shrink-0" />
                                                                    <span className="truncate">{section.title}</span>
                                                                </button>
                                                            </li>
                                                        )
                                                    })}
                                                </ul>
                                            </nav>
                                        </CardContent>
                                    </Card>
                                </div>
                            </motion.aside>

                            {/* Main Content */}
                            <motion.main 
                                variants={itemVariants}
                                className="lg:col-span-3"
                            >
                                <Card className="shadow-sm">
                                    <CardContent className="p-6 sm:p-8 space-y-10">
                                        <section id="acceptance">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">
                                                1. Acceptance of Terms
                                            </h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p className="leading-7">
                                                    By accessing and using {branding?.company_name || 'VoiceBot'}'s services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                                                </p>
                                                <p className="leading-7">
                                                    These terms apply to all visitors, users, and others who access or use our service.
                                                </p>
                                            </div>
                                        </section>

                                        <section id="description">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">2. Description of Service</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    {branding?.company_name || 'VoiceBot'} provides AI-powered voice bot services designed for various applications including customer service, technical support, appointment scheduling, and more.
                                                </p>
                                                <p>
                                                    Our platform includes features such as natural language processing, conversation analytics, multi-language support, and integration capabilities. The specific features and functionality available to you may vary based on your subscription plan and may change over time.
                                                </p>
                                                <p>
                                                    We reserve the right to modify, suspend, or discontinue any aspect of the service at any time, with or without notice.
                                                </p>
                                            </div>
                                </section>

                                        <section id="user-accounts">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">3. User Accounts</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    To access certain features of our service, you must create an account. When creating an account, you agree to:
                                                </p>
                                                <ul className="list-disc pl-6 space-y-2">
                                                    <li>Provide accurate, current, and complete information</li>
                                                    <li>Maintain and promptly update your account information</li>
                                                    <li>Maintain the security of your password and account</li>
                                                    <li>Accept all responsibility for activities that occur under your account</li>
                                                    <li>Notify us immediately of any unauthorized use of your account</li>
                                                </ul>
                                                <p>
                                                    You are solely responsible for all activities that occur under your account. We reserve the right to suspend or terminate accounts that violate these terms.
                                                </p>
                                            </div>
                                </section>

                                        <section id="intellectual-property">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">4. Intellectual Property</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    All content, features, and functionality of our services, including but not limited to text, graphics, logos, icons, images, audio clips, video clips, data compilations, software, and the overall design and "look and feel" of the platform, are the exclusive property of {branding?.company_name || 'VoiceBot'} and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
                                                </p>
                                                <p>
                                                    You are granted a limited, non-exclusive, non-transferable license to access and use our services for your internal business purposes, subject to these terms. You may not reproduce, distribute, modify, create derivative works from, publicly display, publicly perform, republish, download, store, or transmit any of our material without prior written consent.
                                                </p>
                                            </div>
                                </section>

                                        <section id="limitation-liability">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">5. Limitation of Liability</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    To the fullest extent permitted by applicable law, {branding?.company_name || 'VoiceBot'} shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation:
                                                </p>
                                                <ul className="list-disc pl-6 space-y-2">
                                                    <li>Loss of profits, revenue, or business opportunities</li>
                                                    <li>Loss of data or information</li>
                                                    <li>Loss of goodwill or reputation</li>
                                                    <li>Cost of procurement of substitute goods or services</li>
                                                    <li>Any other intangible losses</li>
                                                </ul>
                                                <p>
                                                    These limitations apply regardless of whether the damages arise from use or inability to use the service, unauthorized access, errors or omissions in content, or any other matter related to the service, even if we have been advised of the possibility of such damages.
                                                </p>
                                            </div>
                                </section>

                                        <section id="modifications">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">6. Modifications to Service</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    We reserve the right to modify, suspend, or discontinue the service (or any part thereof), temporarily or permanently, with or without notice, at any time and for any reason.
                                                </p>
                                                <p>
                                                    You agree that {branding?.company_name || 'VoiceBot'} shall not be liable to you or to any third party for any modification, suspension, or discontinuance of the service. We will make reasonable efforts to notify you of significant changes that may materially affect your use of the service.
                                                </p>
                                            </div>
                                </section>

                                        <section id="governing-law">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">7. Governing Law</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    These Terms shall be governed and construed in accordance with the laws of Germany, without regard to its conflict of law provisions.
                                                </p>
                                                <p>
                                                    Any legal action or proceeding arising under these Terms will be brought exclusively in the courts located in Germany, and you hereby irrevocably consent to the personal jurisdiction and venue therein.
                                                </p>
                                                <p>
                                                    Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions will remain in full force and effect.
                                                </p>
                                            </div>
                                </section>

                                        <section id="changes">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">8. Changes to Terms</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will make reasonable efforts to provide at least 30 days' notice prior to any new terms taking effect.
                                                </p>
                                                <p>
                                                    What constitutes a material change will be determined at our sole discretion. By continuing to access or use our service after those revisions become effective, you agree to be bound by the revised terms.
                                                </p>
                                                <p>
                                                    The "Last updated" date at the top of this page indicates when these terms were last revised. We encourage you to review these terms periodically for any changes.
                                                </p>
                                            </div>
                                </section>

                                        <section id="contact">
                                            <h2 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-4">9. Contact Us</h2>
                                            <div className="text-muted-foreground space-y-4 [&>p]:leading-7 [&>ul]:my-4 [&>ul]:ml-6 [&>ul>li]:mt-2">
                                                <p>
                                                    If you have any questions, concerns, or require clarification regarding these Terms of Service, we encourage you to reach out to us through the appropriate channels.
                                                </p>
                                                <div className="rounded-lg border bg-card p-4 not-prose shadow-sm">
                                                    <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                                                        <MailIcon className="h-4 w-4 text-primary" />
                                                        Support Contact
                                                    </p>
                                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                                        For inquiries related to these Terms of Service, please contact us through your respective support channels. Our team is committed to responding to your questions promptly.
                                                    </p>
                                                </div>
                                            </div>
                                        </section>
                            </CardContent>
                        </Card>
                            </motion.main>
                        </div>

                        {/* Footer */}
                        <motion.footer
                            variants={itemVariants}
                            className="mt-12 pt-8 border-t"
                        >
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                <p className="text-xs text-muted-foreground">
                                    {branding?.company_name || 'VoiceBot'} © {new Date().getFullYear()}
                                </p>
                                <nav className="flex items-center gap-4">
                                    <Link 
                                        to="/terms-of-service" 
                                        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        Terms of Service
                                    </Link>
                                    <span className="text-muted-foreground">•</span>
                                    <Link 
                                        to="/privacy-policy" 
                                        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        Privacy Policy
                                    </Link>
                                </nav>
                            </div>
                        </motion.footer>
                    </motion.div>
                        </div>
                    </div>

            {/* Scroll to Top Button */}
            <AnimatePresence>
                {showScrollTop && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        onClick={scrollToTop}
                        className="fixed bottom-6 right-6 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors"
                    >
                        <ArrowUpIcon className="h-4 w-4" />
                    </motion.button>
                )}
            </AnimatePresence>
        </div>
    )
}
